import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { Screen, MatchRule, ScreenIdentifiers } from 'src/app/shared/models/screen.interface';
import { ScreenMasterService } from '../services/screen-master.service';
import { ScreenMasterHelper } from '../helpers/screen-master.helper';
import { ToastService } from '../../../toast/toast.service';
import { ConfirmationService } from 'primeng/api';
import { AuthService } from '../../../auth/login/services/auth.service';

export class ScreenMasterViewModel {
  // Data properties
  screens: Screen[] = [];
  selectedScreen: Screen | null = null;
  isEditMode: boolean = false;
  isCapturing: boolean = false;
  displayDialog: boolean = false;

  // Form fields
  screenId: string = '';
  screenName: string = '';
  description: string = '';
  matchRules: MatchRule[] = [];
  minMatch: number = 2;

  // Rule form for adding/editing rules
  showRuleDialog: boolean = false;
  ruleType: 'title_contains' | 'url_contains' | 'element_exists' | 'heading_contains' | 'breadcrumb_contains' = 'title_contains';
  ruleSelector: string = '';
  ruleText: string = '';
  ruleUrl: string = '';
  editingRuleIndex: number = -1;

  // Captured identifiers for preview
  capturedIdentifiers: ScreenIdentifiers | null = null;

  ruleTypeOptions = [
    { label: 'Page Title Contains', value: 'title_contains' },
    { label: 'URL Contains', value: 'url_contains' },
    { label: 'Element Exists (automation-id)', value: 'element_exists' },
    { label: 'Panel Heading Contains', value: 'heading_contains' },
    { label: 'Breadcrumb Contains', value: 'breadcrumb_contains' }
  ];
  
  constructor(
    private screenMasterService: ScreenMasterService,
    private toastService: ToastService,
    private router: Router,
    private confirmationService: ConfirmationService,
    private authService: AuthService
  ) {}

  /**
   * Initialize viewModel - load data
   */
  init(): void {
    this.loadScreens();
  }

  /**
   * Load all screens from backend
   */
  loadScreens(): void {
    this.screenMasterService.getAllScreenMaster().subscribe({
      next: (screens) => {
        this.screens = screens;
      },
      error: (err) => {
      }
    });
  }

  /**
   * Navigate to create screen mode in test recorder
   */
  showCreateDialog(): void {
    this.router.navigate(['/test-studio/recorder-record'], {
      queryParams: { mode: 'screen-capture' }
    });
  }

  /**
   * Navigate to edit screen mode in test recorder
   */
  showEditDialog(screen: Screen): void {
    if (!screen.id) {
      this.toastService.error('Error', 'Screen ID is missing');
      return;
    }

    // Fetch full screen data from API before navigating
    this.screenMasterService.getScreenMasterById(screen.id).subscribe({
      next: (fullScreenData: Screen) => {
        this.router.navigate(['/test-studio/recorder-record'], {
          queryParams: { mode: 'screen-capture', screenId: fullScreenData.id },
          state: { screen: fullScreenData }
        });
      },
      error: (err: any) => {
        this.toastService.error('Error', err?.error?.message || 'Failed to load screen data');
      }
    });
  }

  /**
   * Capture current screen identifiers from browser
   */
  async captureCurrentScreen(): Promise<void> {
    try {
      this.isCapturing = true;
      this.toastService.info('Capturing', 'Detecting screen identifiers from browser...');

      const identifiers = await ScreenMasterHelper.detectCurrentScreen();
      this.capturedIdentifiers = identifiers;

      // Auto-populate form fields if creating new screen
      if (!this.isEditMode && !this.screenName) {
        this.screenName = identifiers.title || identifiers.mainHeading || 'Untitled Screen';
        
        // Generate screenId from name
        this.screenId = this.screenName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '');
      }

      // Generate suggested rules
      const suggestedRules = ScreenMasterHelper.generateSuggestedRules(identifiers);
      this.matchRules = suggestedRules;

      this.toastService.success('Captured', `Detected ${suggestedRules.length} potential match rules`);
    } catch (error: any) {
      this.toastService.error('Capture Failed', error?.message || 'Could not detect screen. Ensure browser is open.');
    } finally {
      this.isCapturing = false;
    }
  }

  /**
   * Delete screen with confirmation
   */
  deleteScreen(screen: Screen): void {
    if (!screen.id) {
      this.toastService.error('Error', 'Screen ID is missing');
      return;
    }

    this.confirmationService.confirm({
      message: `Are you sure you want to delete screen "${screen.name}"?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.screenMasterService.deleteScreenMaster(screen.id!).subscribe({
          next: () => {
            const index = this.screens.findIndex(s => s.id === screen.id);
            if (index > -1) {
              this.screens.splice(index, 1);
            }
            this.toastService.success('Success', 'Screen deleted successfully');
          },
          error: (err) => {
            this.toastService.error('Error', err?.error?.message || 'Failed to delete screen');
          }
        });
      }
    });
  }

  /**
   * Show add rule dialog
   */
  showAddRuleDialog(): void {
    this.editingRuleIndex = -1;
    this.ruleType = 'title_contains';
    this.ruleSelector = '';
    this.ruleText = '';
    this.ruleUrl = '';
    this.showRuleDialog = true;
  }

  /**
   * Show edit rule dialog
   */
  showEditRuleDialog(rule: MatchRule, index: number): void {
    this.editingRuleIndex = index;
    this.ruleType = rule.type;
    this.ruleSelector = rule.selector || '';
    this.ruleText = rule.text || '';
    this.ruleUrl = rule.url || '';
    this.showRuleDialog = true;
  }

  /**
   * Save rule (add or update)
   */
  saveRule(): void {
    const rule: MatchRule = {
      type: this.ruleType
    };

    // Validate and set appropriate field based on type
    switch (this.ruleType) {
      case 'element_exists':
        if (!this.ruleSelector) {
          this.toastService.warn('Validation Error', 'Please provide data-automation-id value');
          return;
        }
        rule.selector = this.ruleSelector;
        break;
      
      case 'url_contains':
        if (!this.ruleUrl) {
          this.toastService.warn('Validation Error', 'Please provide URL fragment');
          return;
        }
        rule.url = this.ruleUrl;
        break;
      
      case 'title_contains':
      case 'heading_contains':
      case 'breadcrumb_contains':
        if (!this.ruleText) {
          this.toastService.warn('Validation Error', 'Please provide text to match');
          return;
        }
        rule.text = this.ruleText;
        break;
    }

    if (this.editingRuleIndex >= 0) {
      // Update existing rule
      this.matchRules[this.editingRuleIndex] = rule;
    } else {
      // Add new rule
      this.matchRules.push(rule);
    }

    this.showRuleDialog = false;
  }

  /**
   * Delete rule
   */
  deleteRule(index: number): void {
    this.matchRules.splice(index, 1);
  }

  /**
   * Reset form
   */
  resetForm(): void {
    this.screenId = '';
    this.screenName = '';
    this.description = '';
    this.matchRules = [];
    this.minMatch = 2;
    this.capturedIdentifiers = null;
  }

  /**
   * Close dialog
   */
  closeDialog(): void {
    this.displayDialog = false;
    this.resetForm();
  }

  /**
   * Close rule dialog
   */
  closeRuleDialog(): void {
    this.showRuleDialog = false;
  }

  /**
   * Get rules preview text
   */
  getRulesPreview(rules?: MatchRule[]): string {
    try{
      rules = JSON.parse(JSON.stringify(rules));
    } catch(err){}
    if (!rules || rules.length === 0) return 'No rules';
    if (rules.length === 1) {
      const rule = rules[0];
      return `${rule.type}: ${rule.text || rule.selector || rule.url || ''}`.substring(0, 40);
    }
    return `${rules.length} rules`;
  }

  /**
   * Get rule display text
   */
  getRuleDisplayText(rule: MatchRule): string {
    const type = this.ruleTypeOptions.find(opt => opt.value === rule.type)?.label || rule.type;
    const value = rule.text || rule.selector || rule.url || '';
    return `${type}: ${value}`;
  }

  isSystemUser(): boolean {
    const userInfo = this.authService.getUserInfoFromToken();
    return userInfo?.isSystemUser === true;
  }
}
